// Cache buster: v20251127-2
import React, { useState, useEffect } from 'react'
import './App.css'
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom'
import Home from './Home'
import PhotoManager from './PhotoManager'
import AIBlog from './AIBlog'
import AIBlog2 from './AIBlog2'
import BirdingBlog from './BirdingBlog'
import EaglesBlog from './EaglesBlog'
import AcademicsCV from './AcademicsCV'
import PDFViewerTest from './PDFViewerTest'

interface Album {
  id: string
  name: string
  description: string
  coverImage: string
  thumbnailSrcSet?: string
  thumbnailWebpSrcSet?: string
  photoCount: number
  createdDate: string
  hidden?: boolean
}

interface Photo {
  id: string
  albumId: string
  title: string
  url: string
  caption: string
  uploadDate: string
  type?: 'image' | 'video' | 'pdf'
}

// Backend URL configuration:
// - Local: uses http://localhost:3001 (local Node.js backend)
// - Production: uses Lambda API endpoint with S3 URLs
const BACKEND_URL = (typeof window !== 'undefined' && window.location.hostname === 'localhost' 
  ? 'http://localhost:3001' 
  : 'https://8vosl9zwo9.execute-api.us-east-1.amazonaws.com')

// Static albums data as fallback for production (when backend is unavailable)
const FALLBACK_ALBUMS: Album[] = [
  {
    id: 'birding',
    name: 'Birding',
    description: 'Bird photography from around the world',
    coverImage: 'https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/blog-cover-woodpecker.jpg',
    photoCount: 0,
    createdDate: '2024-01-01'
  },
  {
    id: 'eagles',
    name: 'Eagles',
    description: 'Eagle photography at Conowingo Dam',
    coverImage: 'https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/blog-cover-eagles.jpg',
    photoCount: 0,
    createdDate: '2024-01-01'
  },
  {
    id: 'travel',
    name: 'Travel',
    description: 'Travel photography from various destinations',
    coverImage: 'https://bird-created.s3.us-east-1.amazonaws.com/eqsong-images/_DSC2154-Recovered+copy.jpg',
    photoCount: 0,
    createdDate: '2024-01-01'
  }
]

function AppContent (): JSX.Element {
  // Shared state for albums and photos
  const [albums, setAlbums] = useState<Album[]>([])
  const [photos, setPhotos] = useState<Photo[]>([])
  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Function to reload albums - can be called by child components
  const reloadAlbums = (): void => {
    console.log('🔄 App: Reloading albums from backend')
    setRefreshTrigger(prev => prev + 1)
  }

  // Load albums and photos from backend on mount and when refresh is triggered
  useEffect(() => {
    const loadData = async (): Promise<void> => {
      try {
        console.log('🔄 Loading albums from backend:', BACKEND_URL)
        
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout
        
        const response = await fetch(`${BACKEND_URL}/api/albums`, { signal: controller.signal })
        clearTimeout(timeoutId)
        console.log('📡 Albums response:', response.status, response.ok)
        
        if (response.ok) {
          const data = await response.json()
          console.log('📦 Backend albums:', data)
          const backendAlbums = data.albums || []
          console.log('✅ Found', backendAlbums.length, 'albums')
          
          // Fetch photos for each album
          const allPhotos: Photo[] = []
          const updatedAlbums: Album[] = []
          
          for (const album of backendAlbums) {
            try {
              console.log('📸 Fetching photos for album:', album.id)
              const photosResponse = await fetch(`${BACKEND_URL}/api/photos/${album.id}`)
              if (photosResponse.ok) {
                const photosData = await photosResponse.json()
                console.log(`📷 Photos for ${album.id}:`, photosData)
                const photos = photosData.photos || []
                allPhotos.push(...photos)
                
                // Create album object with photo count and cover image
                // Use first image (not video or pdf) as cover
                const coverPhoto = photos.find((p: any) => p.type !== 'video' && p.type !== 'pdf') || photos[0]
                updatedAlbums.push({
                  id: album.id,
                  name: album.name || album.id,
                  description: album.description || '',
                  coverImage: coverPhoto?.thumbnailUrl || coverPhoto?.url || 'https://via.placeholder.com/300',
                  thumbnailSrcSet: coverPhoto?.thumbnailSrcSet,
                  thumbnailWebpSrcSet: coverPhoto?.thumbnailWebpSrcSet,
                  photoCount: photos.length,
                  createdDate: album.createdDate || new Date().toISOString().split('T')[0],
                  hidden: album.hidden || false
                })
              }
            } catch (err) {
              console.log(`No photos endpoint for ${album.id} (may require auth)`)
            }
          }
          
          console.log('🖼️ Total photos loaded:', allPhotos.length)
          console.log('✨ Updated albums:', updatedAlbums)
          setAlbums(updatedAlbums)
          setPhotos(allPhotos)
        } else {
          console.error('❌ Failed to fetch albums:', response.status)
          setAlbums(FALLBACK_ALBUMS)
        }
      } catch (err) {
        console.error('❌ Error loading data:', err)
        console.log('⚠️ Using fallback albums (backend unavailable)')
        setAlbums(FALLBACK_ALBUMS)
      }
    }
    
    loadData()
  }, [refreshTrigger])

  return (
    <div className="App">
      <nav style={{
        background: 'white',
        padding: '15px 20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        gap: '15px',
        justifyContent: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        flexWrap: 'wrap'
      }}>
        <Link to="/" style={{
          padding: '10px 20px',
          background: '#f0f0f0',
          color: '#333',
          textDecoration: 'none',
          borderRadius: '6px',
          fontWeight: '600',
          fontSize: '0.95rem',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}>
          🏠 Home
        </Link>
        <Link to="/photo-manager" style={{
          padding: '10px 20px',
          background: '#f0f0f0',
          color: '#333',
          textDecoration: 'none',
          borderRadius: '6px',
          fontWeight: '600',
          fontSize: '0.95rem',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}>
          🔐 Photo Manager
        </Link>
        <Link to="/blog/ai" style={{
          padding: '10px 20px',
          background: '#f0f0f0',
          color: '#333',
          textDecoration: 'none',
          borderRadius: '6px',
          fontWeight: '600',
          fontSize: '0.95rem',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}>
          🤖 AI Blog I
        </Link>
        <Link to="/blog/ai2" style={{
          padding: '10px 20px',
          background: '#f0f0f0',
          color: '#333',
          textDecoration: 'none',
          borderRadius: '6px',
          fontWeight: '600',
          fontSize: '0.95rem',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}>
          🤖 AI Blog II
        </Link>
        <Link to="/academics" style={{
          padding: '10px 20px',
          background: '#f0f0f0',
          color: '#333',
          textDecoration: 'none',
          borderRadius: '6px',
          fontWeight: '600',
          fontSize: '0.95rem',
          transition: 'all 0.3s ease',
          cursor: 'pointer'
        }}>
          👨‍🎓 Academics
        </Link>
      </nav>

      <Routes>
        <Route path="/" element={<Home albums={albums} photos={photos} />} />
        <Route path="/photo-manager" element={<PhotoManager albums={albums} setAlbums={setAlbums} photos={photos} setPhotos={setPhotos} onAlbumsChange={reloadAlbums} />} />
        <Route path="/blog/ai" element={<AIBlog />} />
        <Route path="/blog/ai2" element={<AIBlog2 />} />
        <Route path="/blog/birding" element={<BirdingBlog />} />
        <Route path="/blog/eagles" element={<EaglesBlog />} />
        <Route path="/academics" element={<AcademicsCV />} />
        <Route path="/pdf-viewer-test" element={<PDFViewerTest />} />
      </Routes>
    </div>
  )
}

function App (): JSX.Element {
  return (
    <Router>
      <AppContent />
    </Router>
  )
}

export default App

